import { useState, useCallback } from 'react';
import { useAnimatedNavigate } from '../hooks/useAnimatedNavigate';
import { useMobileNav } from '../hooks/useMobileNav';
import { ConversationSidebar } from '../components/ConversationSidebar';
import { MobileNavToggle, SidebarBackdrop } from '../components/MobileNav';
import { DarkModeToggle } from '../components/DarkModeToggle';
import { UserAvatarDropdown } from '../components/UserAvatarDropdown';
import { ProjectCard } from '../components/projects/ProjectCard';
import { NewProjectDialog } from '../components/projects/NewProjectDialog';
import { trpc } from '../api/trpc';

type ProjectStatus = 'active' | 'archived';

export function ProjectsPage() {
  const navigate = useAnimatedNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const { isMobile, mobileNavOpen, openNav, closeNav } = useMobileNav();
  const [activeTab, setActiveTab] = useState<ProjectStatus>('active');
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  void showSearch; // Used by sidebar onSearch callback

  const projects = trpc.projects.list.useQuery({ status: activeTab });
  const restoreProject = trpc.projects.updateStatus.useMutation();
  const utils = trpc.useUtils();

  const handleProjectCreated = useCallback((projectId: string) => {
    setShowNewDialog(false);
    navigate(`/projects/${projectId}`);
  }, [navigate]);

  return (
    <div className="chat-layout">
      <div className="chat-body">
        <ConversationSidebar
          activeId={null}
          onSelect={(id: string) => { closeNav(); navigate(`/?c=${id}`); }}
          onNew={() => { closeNav(); navigate('/'); }}
          onSearch={() => { closeNav(); setShowSearch(true); }}
          collapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(c => !c)}
          onNavigateToProjects={() => { closeNav(); navigate('/projects'); }}
          onNavigateToProject={(id: string) => { closeNav(); navigate(`/projects/${id}`); }}
          onNavigateToAgents={() => { closeNav(); navigate('/agents'); }}
          onNavigateToAgent={(id: string) => { closeNav(); navigate(`/agents/${id}`); }}
          onNavigateHome={() => { closeNav(); navigate('/'); }}
          isMobile={isMobile}
          mobileOpen={mobileNavOpen}
          onMobileClose={closeNav}
        />
        {isMobile && mobileNavOpen && <SidebarBackdrop onClose={closeNav} />}

        <div className="chat-column">
          <div className="topbar">
            {isMobile && <MobileNavToggle onOpen={openNav} />}
            <div className="topbar-title-area">
              <span className="topbar-title"></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <DarkModeToggle />
              <UserAvatarDropdown />
            </div>
          </div>

          <main className="chat-main">
            <div className="page-scroll">
              <div className="page-inner">
                <div className="page-header">
                  <div className="page-title">Projects</div>
                  <button className="btn btn-recommended" onClick={() => setShowNewDialog(true)}>
                    New Project
                  </button>
                </div>

                <div className="page-tabs">
                  {(['active', 'archived'] as const).map(status => (
                    <button
                      key={status}
                      className={`page-tab ${activeTab === status ? 'active' : ''}`}
                      onClick={() => setActiveTab(status)}
                    >
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                      {status === activeTab && projects.data && (
                        <span className="page-tab-count">{projects.data.length}</span>
                      )}
                    </button>
                  ))}
                </div>

                {projects.isLoading && (
                  <div className="proj-empty">Loading projects...</div>
                )}

                {projects.data && projects.data.length === 0 && (
                  <div className="proj-empty">
                    <div className="proj-empty-title">
                      {activeTab === 'active' ? 'Create your first project' : `No ${activeTab} projects`}
                    </div>
                    {activeTab === 'active' && (
                      <div className="proj-empty-desc">
                        Projects help you organize related conversations, reference files, and artifacts into focused workspaces.
                      </div>
                    )}
                    {activeTab === 'active' && (
                      // Secondary here: the header "New Project" is the single
                      // amber CTA on this screen (OBSIDIAN — one amber per screen).
                      <button className="btn btn-secondary" onClick={() => setShowNewDialog(true)}>
                        New Project
                      </button>
                    )}
                  </div>
                )}

                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {projects.data?.map((project: any) => (
                  <div key={project.id} style={{ position: 'relative' }}>
                    <ProjectCard
                      id={project.id}
                      title={project.title}
                      description={project.description}
                      conversationCount={project._count.conversations}
                      fileCount={project._count.kbFiles}
                      updatedAt={project.updatedAt as unknown as string}
                      participants={project.participants}
                      onClick={() => activeTab === 'active' ? navigate(`/projects/${project.id}`) : undefined}
                    />
                    {activeTab === 'archived' && (
                      <button
                        className="btn btn-primary btn-sm"
                        style={{ position: 'absolute', top: 16, right: 16, fontSize: 11, padding: '4px 10px' }}
                        onClick={async (e) => {
                          e.stopPropagation();
                          await restoreProject.mutateAsync({ projectId: project.id, status: 'active' });
                          void utils.projects.list.invalidate();
                        }}
                      >
                        Restore
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </main>
        </div>
      </div>

      {showNewDialog && (
        <NewProjectDialog
          onClose={() => setShowNewDialog(false)}
          onCreated={handleProjectCreated}
        />
      )}
    </div>
  );
}
